/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode,
} from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useEncryption } from './EncryptionContext';
import {
  subscribeToConversations,
  subscribeToUnreadCount,
  getOrCreateConversation,
  sendMessage as dmSendMessage,
  markAsRead as dmMarkAsRead,
  deleteMessage as dmDeleteMessage,
  archiveConversation as dmArchiveConversation,
  unarchiveConversation as dmUnarchiveConversation,
  toggleFavorite as dmToggleFavorite,
  muteConversation as dmMuteConversation,
  blockUser as dmBlockUser,
  setTypingStatus,
} from '@/services/dm';
import type { DMContact, DMContextValue, SendMessageInput, ConversationFilter } from '@/types/dm';
import { incrementMetric } from '@/services/engagementMetrics';
import { useAuthCleanup } from './AuthCleanupContext';
import { useToast } from './ToastContext';

// ==================== LocalStorage Cache Helpers ====================

const CACHE_KEY_PREFIX = 'dm_cache_';

interface CachedData<T> {
  data: T;
  timestamp: number;
}

// Marker for serialized Timestamp objects
interface SerializedTimestamp {
  __type: 'Timestamp';
  seconds: number;
  nanoseconds: number;
}

function getCacheKey(userId: string): string {
  return `${CACHE_KEY_PREFIX}${userId}`;
}

/**
 * Custom JSON replacer that converts Firestore Timestamps to a serializable format
 */
function timestampReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Timestamp) {
    return {
      __type: 'Timestamp',
      seconds: value.seconds,
      nanoseconds: value.nanoseconds,
    } as SerializedTimestamp;
  }
  return value;
}

/**
 * Custom JSON reviver that rehydrates serialized Timestamps back to Firestore Timestamp objects
 */
function timestampReviver(_key: string, value: unknown): unknown {
  if (value && typeof value === 'object' && (value as SerializedTimestamp).__type === 'Timestamp') {
    const serialized = value as SerializedTimestamp;
    return new Timestamp(serialized.seconds, serialized.nanoseconds);
  }
  return value;
}

function getCachedConversations(userId: string): DMContact[] | null {
  try {
    const cached = localStorage.getItem(getCacheKey(userId));
    if (!cached) return null;

    const parsed: CachedData<DMContact[]> = JSON.parse(cached, timestampReviver);
    // Return cached data even if expired (will be updated by subscription)
    // This allows instant display while fresh data loads
    return parsed.data;
  } catch {
    return null;
  }
}

function setCachedConversations(userId: string, conversations: DMContact[]): void {
  try {
    const cacheData: CachedData<DMContact[]> = {
      data: conversations,
      timestamp: Date.now(),
    };
    localStorage.setItem(getCacheKey(userId), JSON.stringify(cacheData, timestampReplacer));
  } catch {
    // localStorage might be full or unavailable, ignore
  }
}

// ==================== Context ====================

interface DMProviderProps {
  children: ReactNode;
}

const DMContext = createContext<DMContextValue | null>(null);

export function DMProvider({ children }: DMProviderProps) {
  const { authState } = useAuth();
  const { ready: encryptionReady } = useEncryption();
  const { registerCleanup } = useAuthCleanup();
  const { showToast } = useToast();

  // State
  const [conversations, setConversations] = useState<DMContact[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const unsubscribeConversations = useRef<(() => void) | null>(null);
  const unsubscribeUnread = useRef<(() => void) | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingConversationIdRef = useRef<string | null>(null);

  // Register cleanup function for logout
  // This clears all user-specific DM data when user logs out
  useEffect(() => {
    registerCleanup('dm-context', () => {
      // Unsubscribe from all Firestore listeners
      unsubscribeConversations.current?.();
      unsubscribeConversations.current = null;
      unsubscribeUnread.current?.();
      unsubscribeUnread.current = null;

      // Clear typing timeout and active typing status
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      typingConversationIdRef.current = null;

      // Reset all state to initial values
      setConversations([]);
      setActiveConversationId(null);
      setTotalUnreadCount(0);
      setLoading(false);
      setError(null);
    });
  }, [registerCleanup]);

  // Get current user ID
  const userId = authState.status === 'authenticated' ? authState.user.uid : null;
  const userProfile = authState.status === 'authenticated' ? authState.profile : null;

  // Subscribe to conversations when user is authenticated
  useEffect(() => {
    if (!userId) {
      setConversations([]);
      setTotalUnreadCount(0);
      setLoading(false);
      return;
    }

    // Try to load from cache first for instant display
    const cached = getCachedConversations(userId);
    if (cached && cached.length > 0) {
      setConversations(cached);
      setLoading(false); // Show cached data immediately
    } else {
      setLoading(true);
    }

    // Subscribe to conversations (will update with fresh data)
    unsubscribeConversations.current = subscribeToConversations(userId, (contacts) => {
      setConversations(contacts);
      setLoading(false);
      // Update cache with fresh data
      setCachedConversations(userId, contacts);
    });

    // Subscribe to unread count
    unsubscribeUnread.current = subscribeToUnreadCount(userId, (count) => {
      setTotalUnreadCount(count);
    });

    return () => {
      unsubscribeConversations.current?.();
      unsubscribeUnread.current?.();
      // Clean up typing timeout on unmount to prevent memory leaks
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
    // Re-subscribe when encryption becomes ready so conversation previews
    // can be decrypted (initial snapshot may fire before encryption initializes).
  }, [userId, encryptionReady]);

  // Open a conversation
  const openConversation = useCallback(
    (conversationId: string) => {
      setActiveConversationId(conversationId);

      // Optimistically update unread count immediately for responsive UI
      const conversation = conversations.find((c) => c.conversation.id === conversationId);
      if (conversation && conversation.metadata.unreadCount > 0) {
        // Subtract this conversation's unread count from total
        setTotalUnreadCount((prev) => Math.max(0, prev - conversation.metadata.unreadCount));

        // Also update the conversation's local metadata
        setConversations((prev) =>
          prev.map((c) =>
            c.conversation.id === conversationId
              ? { ...c, metadata: { ...c.metadata, unreadCount: 0 } }
              : c
          )
        );
      }

      // Mark as read on the server (subscription will confirm)
      if (userId) {
        dmMarkAsRead(conversationId, userId).catch(console.error);
      }
    },
    [userId, conversations]
  );

  // Close the active conversation
  const closeConversation = useCallback(() => {
    setActiveConversationId(null);
  }, []);

  // Send a message
  const sendMessage = useCallback(
    async (conversationId: string, message: SendMessageInput): Promise<void> => {
      if (!userId || !userProfile) {
        throw new Error('User not authenticated');
      }

      try {
        await dmSendMessage(conversationId, userId, message, userProfile.displayName);

        // Track engagement metrics for donation triggers
        incrementMetric(userId, 'messagesThisDay').catch(console.error);
        incrementMetric(userId, 'messagesThisWeek').catch(console.error);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        throw err;
      }
    },
    [userId, userProfile]
  );

  // Mark conversation as read
  const markAsRead = useCallback(
    async (conversationId: string): Promise<void> => {
      if (!userId) return;
      await dmMarkAsRead(conversationId, userId);
    },
    [userId]
  );

  // Delete a message
  const deleteMessage = useCallback(
    async (conversationId: string, messageId: string): Promise<void> => {
      await dmDeleteMessage(conversationId, messageId);
    },
    []
  );

  // Start a new conversation with a member
  const startConversation = useCallback(
    async (memberId: string, initialMessage?: string): Promise<string> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const conversationId = await getOrCreateConversation(userId, memberId);

      if (initialMessage && userProfile) {
        await dmSendMessage(
          conversationId,
          userId,
          { text: initialMessage },
          userProfile.displayName
        );
      }

      setActiveConversationId(conversationId);
      return conversationId;
    },
    [userId, userProfile]
  );

  // Archive a conversation
  const archiveConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      if (!userId) return;

      setConversations((prev) =>
        prev.map((c) =>
          c.conversation.id === conversationId
            ? { ...c, metadata: { ...c.metadata, isArchived: true } }
            : c
        )
      );

      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
      }

      showToast({ message: 'Conversation archived', type: 'info' });

      try {
        await dmArchiveConversation(conversationId, userId);
      } catch {
        setConversations((prev) =>
          prev.map((c) =>
            c.conversation.id === conversationId
              ? { ...c, metadata: { ...c.metadata, isArchived: false } }
              : c
          )
        );
        showToast({ message: 'Failed to archive conversation', type: 'error' });
      }
    },
    [userId, activeConversationId, showToast]
  );

  // Unarchive a conversation
  const unarchiveConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      if (!userId) return;

      setConversations((prev) =>
        prev.map((c) =>
          c.conversation.id === conversationId
            ? { ...c, metadata: { ...c.metadata, isArchived: false } }
            : c
        )
      );

      try {
        await dmUnarchiveConversation(conversationId, userId);
      } catch {
        setConversations((prev) =>
          prev.map((c) =>
            c.conversation.id === conversationId
              ? { ...c, metadata: { ...c.metadata, isArchived: true } }
              : c
          )
        );
        showToast({ message: 'Failed to unarchive conversation', type: 'error' });
      }
    },
    [userId, showToast]
  );

  // Toggle favorite status
  const toggleFavorite = useCallback(
    async (conversationId: string): Promise<void> => {
      if (!userId) return;

      setConversations((prev) =>
        prev.map((c) =>
          c.conversation.id === conversationId
            ? { ...c, metadata: { ...c.metadata, isFavorite: !c.metadata.isFavorite } }
            : c
        )
      );

      try {
        await dmToggleFavorite(conversationId, userId);
      } catch {
        // Rollback: flip isFavorite back
        setConversations((prev) =>
          prev.map((c) =>
            c.conversation.id === conversationId
              ? { ...c, metadata: { ...c.metadata, isFavorite: !c.metadata.isFavorite } }
              : c
          )
        );
        showToast({ message: 'Failed to update favorite', type: 'error' });
      }
    },
    [userId, showToast]
  );

  // Mute/unmute a conversation
  const muteConversation = useCallback(
    async (conversationId: string, muted: boolean): Promise<void> => {
      if (!userId) return;

      setConversations((prev) =>
        prev.map((c) =>
          c.conversation.id === conversationId
            ? { ...c, metadata: { ...c.metadata, isMuted: muted } }
            : c
        )
      );

      try {
        await dmMuteConversation(conversationId, userId, muted);
      } catch {
        setConversations((prev) =>
          prev.map((c) =>
            c.conversation.id === conversationId
              ? { ...c, metadata: { ...c.metadata, isMuted: !muted } }
              : c
          )
        );
        showToast({ message: 'Failed to update mute setting', type: 'error' });
      }
    },
    [userId, showToast]
  );

  // Block/unblock a user
  const blockUser = useCallback(
    async (conversationId: string, blocked: boolean): Promise<void> => {
      if (!userId) return;
      await dmBlockUser(conversationId, userId, blocked);
    },
    [userId]
  );

  // Set typing status with debounce.
  // Tracks active conversation to clear typing on the old conversation when switching.
  const setTyping = useCallback(
    (conversationId: string, isTyping: boolean) => {
      if (!userId) return;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      if (isTyping) {
        // If switching conversations while typing, clear the old one first
        if (typingConversationIdRef.current && typingConversationIdRef.current !== conversationId) {
          setTypingStatus(typingConversationIdRef.current, userId, false).catch(console.error);
        }
        typingConversationIdRef.current = conversationId;
      } else {
        typingConversationIdRef.current = null;
      }

      setTypingStatus(conversationId, userId, isTyping).catch(console.error);

      // Auto-clear typing after 3 seconds
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          typingTimeoutRef.current = null;
          typingConversationIdRef.current = null;
          setTypingStatus(conversationId, userId, false).catch(console.error);
        }, 3000);
      }
    },
    [userId]
  );

  // Refresh conversations
  const refreshConversations = useCallback(async (): Promise<void> => {
    // The subscription handles real-time updates, but we can force a refresh
    // by temporarily unsubscribing and resubscribing
    if (!userId) return;

    unsubscribeConversations.current?.();

    setLoading(true);
    unsubscribeConversations.current = subscribeToConversations(userId, (contacts) => {
      setConversations(contacts);
      setLoading(false);
      // Update cache with fresh data (consistent with main subscription)
      setCachedConversations(userId, contacts);
    });
  }, [userId]);

  const value: DMContextValue = useMemo(
    () => ({
      conversations,
      activeConversationId,
      totalUnreadCount,
      loading,
      error,
      openConversation,
      closeConversation,
      sendMessage,
      markAsRead,
      deleteMessage,
      startConversation,
      archiveConversation,
      unarchiveConversation,
      toggleFavorite,
      muteConversation,
      blockUser,
      setTyping,
      refreshConversations,
    }),
    [
      conversations,
      activeConversationId,
      totalUnreadCount,
      loading,
      error,
      openConversation,
      closeConversation,
      sendMessage,
      markAsRead,
      deleteMessage,
      startConversation,
      archiveConversation,
      unarchiveConversation,
      toggleFavorite,
      muteConversation,
      blockUser,
      setTyping,
      refreshConversations,
    ]
  );

  return <DMContext.Provider value={value}>{children}</DMContext.Provider>;
}

export function useDM(): DMContextValue {
  const context = useContext(DMContext);
  if (!context) {
    throw new Error('useDM must be used within a DMProvider');
  }
  return context;
}

/**
 * Hook to get the active conversation details
 */
export function useActiveConversation(): DMContact | null {
  const { conversations, activeConversationId } = useDM();

  if (!activeConversationId) return null;

  return conversations.find((c) => c.conversation.id === activeConversationId) || null;
}

/**
 * Hook to filter conversations
 */
export function useFilteredConversations(filter: ConversationFilter): DMContact[] {
  const { conversations } = useDM();

  switch (filter) {
    case 'unread':
      return conversations.filter((c) => !c.metadata.isArchived && c.metadata.unreadCount > 0);
    case 'favorites':
      return conversations.filter((c) => !c.metadata.isArchived && c.metadata.isFavorite);
    case 'archived':
      return conversations.filter((c) => c.metadata.isArchived);
    default:
      return conversations.filter((c) => !c.metadata.isArchived);
  }
}
